/**
 * Colormaps for the spectrogram overlay.
 *
 * Tiles arrive from the server as 8-bit grayscale, so the colormap and the
 * displayed dB window are both applied here — changing either rebuilds a
 * 256-entry lookup table and recolours the cached tiles, with no refetch.
 */

/**
 * Control points sampled at even intervals from 0 to 1. Linear interpolation
 * between nine stops is visually indistinguishable from the full 256-entry
 * originals at the sizes a spectrogram tile is drawn.
 */
const RAMPS = {
  // Perceptually uniform, dark at the floor — reads well over a dark timeline
  // and doesn't invent structure the way a rainbow ramp does.
  magma: [
    [0, 0, 4], [28, 16, 68], [79, 18, 123], [129, 37, 129], [181, 54, 122],
    [229, 80, 100], [251, 135, 97], [254, 194, 135], [252, 253, 191],
  ],
  viridis: [
    [68, 1, 84], [72, 40, 120], [62, 74, 137], [49, 104, 142], [38, 130, 142],
    [31, 158, 137], [53, 183, 121], [109, 205, 89], [253, 231, 37],
  ],
  gray: [
    [0, 0, 0], [32, 32, 32], [64, 64, 64], [96, 96, 96], [128, 128, 128],
    [160, 160, 160], [192, 192, 192], [224, 224, 224], [255, 255, 255],
  ],
};

export const COLORMAP_NAMES = Object.keys(RAMPS);

function sampleRamp(ramp, t) {
  const clamped = t <= 0 ? 0 : t >= 1 ? 1 : t;
  const position = clamped * (ramp.length - 1);
  const index = Math.min(ramp.length - 2, Math.floor(position));
  const fraction = position - index;

  const from = ramp[index];
  const to = ramp[index + 1];
  return [
    Math.round(from[0] + (to[0] - from[0]) * fraction),
    Math.round(from[1] + (to[1] - from[1]) * fraction),
    Math.round(from[2] + (to[2] - from[2]) * fraction),
  ];
}

/**
 * Build the byte-to-colour table.
 *
 * A tile pixel encodes dB linearly between the manifest's `dbFloor` and
 * `dbCeiling`. The display window is a sub-range of that, so re-ranging is a
 * table rebuild rather than a refetch — the encoded floor is usually well below
 * anything worth looking at.
 *
 * @param {string} name - one of COLORMAP_NAMES
 * @param {Object} range
 * @param {number} range.dbFloor - dB at pixel value 0, from the manifest
 * @param {number} range.dbCeiling - dB at pixel value 255, from the manifest
 * @param {number} range.displayFloor - dB shown as the bottom of the ramp
 * @param {number} range.displayCeiling - dB shown as the top of the ramp
 * @returns {Uint8Array} 256 × 3 RGB entries
 */
export function buildColormapLut(name, { dbFloor, dbCeiling, displayFloor, displayCeiling }) {
  const ramp = RAMPS[name] || RAMPS.magma;
  const lut = new Uint8Array(256 * 3);

  const encodedSpan = dbCeiling - dbFloor;
  const displaySpan = displayCeiling - displayFloor;

  for (let value = 0; value < 256; value++) {
    const db = dbFloor + (value / 255) * encodedSpan;
    const t = displaySpan === 0 ? 0 : (db - displayFloor) / displaySpan;
    const [r, g, b] = sampleRamp(ramp, t);

    lut[value * 3] = r;
    lut[value * 3 + 1] = g;
    lut[value * 3 + 2] = b;
  }

  return lut;
}
